/**
 * Очередь сделок и карточка сделки (#766, PR4a) — контракт ответов и запросов
 * маршрутов `/api/v1/deals*`. План: docs/plans/2026-10-04-deal-state-pr4-deal-queue.md
 * (D4 — видимость, D5 — маршруты, D7 — задача в CRM).
 *
 * Состояние сделки (`dealStateSchema`) здесь не дублируется: карточка отдаёт
 * его вариантом `visibleDealStateSchema`, где ссылки на разговоры, которых
 * зритель не видит, убраны (Q6, решение владельца 04.10): `quote`, `recordId`,
 * `atSec`, `sourceRecordId` необязательны, а `sourceHidden: true` говорит
 * интерфейсу показать «из разговора, к которому у вас нет доступа». Текст
 * состояния остаётся целиком — кроме управляемого субъекта без `evidence.text`
 * (`textWithheld`, см. `visibleDealStateSchema`).
 *
 * Чего нет в ответах: `crm_responsible_id` (внутреннее — ответственный
 * задачи) и `crm_task_claim` (заявка, не данные).
 */
import { z } from 'zod/v4';
import {
  DEAL_ENTITY_TYPES,
  DEAL_STATE_LIMITS,
  DEAL_STATE_TASK_STATUSES,
  DEAL_STATUSES,
  dealCommitmentSchema,
  dealNextActionSchema,
  dealObjectionSchema,
  dealRiskReasonSchema,
  dealStateSchema,
} from './deal-state';
import { agentScreenIncludeDemoSchema } from './agents';
import { lockedDemoOnlySchema } from './common';
import { CRM_ENTITY_ID_PATTERN, recordTypeSchema } from './records';

const dealStatusSchema = z.enum(DEAL_STATUSES);
const entityTypeSchema = z.enum(DEAL_ENTITY_TYPES);
const isoDateTimeSchema = z.iso.datetime({ offset: true });
/** Id строки `deal_states` — `crypto.randomUUID()`; `guid`, как `previousStateId` в состоянии. */
const stateIdSchema = z.guid();
const entityIdSchema = z.string().min(1).max(128);
const projectIdSchema = z.string().min(1).max(128);
const recordIdSchema = z.string().min(1).max(128);

/** Фильтр очереди: корзина состояния или «закрытые в CRM» — не статус состояния. */
export const DEAL_QUEUE_STATUS_FILTERS = [...DEAL_STATUSES, 'closed'] as const;

// ============================================================================
// Очередь: GET /api/v1/deals
// ============================================================================

export const dealQueueQuerySchema = z.object({
  /** Без значения — три корзины и таблица по ним; закрытые — только `closed`. */
  status: z.enum(DEAL_QUEUE_STATUS_FILTERS).optional(),
  projectId: projectIdSchema.optional(),
  /** Непрозрачный курсор страницы: клиент отдаёт его назад как получил. */
  cursor: z.string().min(1).max(1024).optional(),
  /**
   * The screen's demo policy (`useDemoFilterFromAuth`), same as every other
   * screen: `'no'` once the org has real data — the demo deals (installation
   * `DEMO_DEAL_INSTALLATION_ID`) leave the buckets, the counters and the rows.
   * The schema is not strict, so an older edge simply ignores the parameter.
   */
  includeDemo: agentScreenIncludeDemoSchema,
});
export type DealQueueQuery = z.infer<typeof dealQueueQuerySchema>;

/**
 * `:stateId` всех маршрутов сделок — RFC 4122 (`gen_random_uuid()`), иначе 404
 * «нет такой сделки». Строже `stateIdSchema` ответа: принимать id, которого
 * Postgres не выдаёт, незачем.
 */
export const dealStateIdParamSchema = z.uuid();

// ============================================================================
// Состояние по сущности CRM: GET /api/v1/deals/by-crm-entity (#745)
// ============================================================================

/**
 * Сделка или лид, на карточке которых открыта вкладка Aelo в Bitrix24. Номер —
 * номер портала (`ID` в Bitrix24), поэтому маршрут работает только во
 * встроенной сессии: установка — её, не из запроса.
 */
export const dealByCrmEntityQuerySchema = z.object({
  entityType: z.enum(DEAL_ENTITY_TYPES),
  entityId: z.string().regex(CRM_ENTITY_ID_PATTERN),
});
export type DealByCrmEntityQuery = z.infer<typeof dealByCrmEntityQuerySchema>;

/**
 * Голова цепочки этой сущности, которую зритель видит (та же граница, что у
 * карточки), — самая свежая (`covered_until`, как «последний разговор»
 * карточки и порядок очереди). Видимой нет — `found: false`, неотличимо от
 * «состояния нет». `stateId` — по схеме `:stateId` карточки: вкладка
 * открывает карточку ровно этим id, и ответ не обещает id, который карточка
 * отвергла бы 404.
 */
export const dealByCrmEntityResponseSchema = z.discriminatedUnion('found', [
  z.object({ found: z.literal(true), stateId: dealStateIdParamSchema }),
  z.object({ found: z.literal(false) }),
]);
export type DealByCrmEntityResponse = z.infer<typeof dealByCrmEntityResponseSchema>;

/** GET /api/v1/deals/:stateId/conversations — `before`: курсор из `nextCursor` прошлой страницы. */
export const dealConversationsQuerySchema = z.object({
  before: z.string().min(1).max(1024).optional(),
});
export type DealConversationsQuery = z.infer<typeof dealConversationsQuerySchema>;

/**
 * Что внутри курсора ленты (непрозрачного для клиента): нормализованный
 * `created_at` строки и её id — keyset страницы.
 */
export const dealFeedCursorSchema = z.object({
  k: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/),
  i: z.string().min(1).max(128),
});

/**
 * Снимок сделки в CRM на момент пересчёта (Q2) — интерфейс подписывает его
 * «на <дата>». Всё `null`, если сделку не прочитали (Uspacy, сбой чтения).
 * Ответственного (`crm_responsible_id`) в ответах нет.
 */
export const dealSnapshotSchema = z.object({
  title: z.string().nullable(),
  amount: z.number().nullable(),
  currency: z.string().nullable(),
  pipelineId: z.string().nullable(),
  stageId: z.string().nullable(),
  stageClosed: z.boolean().nullable(),
  at: isoDateTimeSchema.nullable(),
});
export type DealSnapshot = z.infer<typeof dealSnapshotSchema>;

export const dealQueueItemSchema = z.object({
  stateId: stateIdSchema,
  entityType: entityTypeSchema,
  entityId: entityIdSchema,
  projectId: projectIdSchema,
  installationId: z.string().min(1).max(128),
  status: dealStatusSchema,
  closeChance: dealStateSchema.shape.closeChance,
  nextAction: dealNextActionSchema.pick({ kind: true, dueBy: true }),
  /**
   * Почему такой статус — текст модели (`riskReason.text`), без цитаты и
   * ссылки на разговор. Нет у состояния без причины и у зрителя без
   * `evidence.text`.
   */
  riskReasonText: dealRiskReasonSchema.shape.text.optional(),
  /** `covered_until` цепочки — самый свежий разговор, а не `conversation_at` позднего вливания. */
  lastConversationAt: isoDateTimeSchema,
  /**
   * Нет у управляемого субъекта без `team.scores`: пол шлюза возможностей
   * снимает поле целиком, и ответ без него остаётся валидным.
   */
  agentName: z.string().nullable().optional(),
  snapshot: dealSnapshotSchema,
  /** Вендор CRM установки (`bitrix24`, `uspacy`); по нему интерфейс прячет кнопку задачи. */
  vendor: z.string().nullable(),
});
export type DealQueueItem = z.infer<typeof dealQueueItemSchema>;

/**
 * Одна сделка CRM — ключ, по которому очередь сворачивает головы цепочек:
 * (установка, тип сущности, id сущности). Единственная реализация: сервер
 * схлопывает по ней страницу, интерфейс — склеивает страницы.
 */
export const dealIdentity = (
  item: Pick<DealQueueItem, 'installationId' | 'entityType' | 'entityId'>
): string => `${item.installationId}|${item.entityType}|${item.entityId}`;

/** Сумма корзины по одной валюте: валюты между собой не складываются. */
export const dealBucketAmountSchema = z.object({
  currency: z.string().min(1),
  total: z.number(),
});
export type DealBucketAmount = z.infer<typeof dealBucketAmountSchema>;

export const dealQueueBucketSchema = z.object({
  count: z.number().int().min(0),
  amounts: z.array(dealBucketAmountSchema),
});
export type DealQueueBucket = z.infer<typeof dealQueueBucketSchema>;

export const dealQueueResponseSchema = z.object({
  /** Все три корзины присутствуют всегда (record с enum-ключом полон и закрыт). */
  buckets: z.record(dealStatusSchema, dealQueueBucketSchema),
  /** Закрытые в CRM сделки — не в корзинах, отдельным счётчиком. */
  closed: z.object({ count: z.number().int().min(0) }),
  items: z.array(dealQueueItemSchema),
  nextCursor: z.string().nullable(),
  /** Предохранитель обхода голов (D4) сработал: корзины посчитаны по неполному набору. */
  truncated: z.boolean(),
  demoOnly: lockedDemoOnlySchema,
});
export type DealQueueResponse = z.infer<typeof dealQueueResponseSchema>;

/**
 * Снимок порядка очереди в KV (#1706) — не ответ API, а то, что первая
 * страница оставляет страницам по курсору: строки по порядку, корзины,
 * счётчик закрытых, `truncated`. Строка — пара `[id состояния, dealIdentity]`:
 * по id страница читает свои строки, по идентичности обход, которому срез
 * вернулся неполным, узнаёт уже показанные сделки — id состояния у той же
 * сделки к тому времени может быть другим (#1955). `alsoServed` — идентичности
 * уже показанных сделок, которых нет среди строк до курсора (сортируются
 * теперь после него либо не в списке): страница по снимку их пропускает, а
 * обход, продолженный с этого снимка, считает показанными. `query` — параметры
 * запроса и отпечаток границы видимости зрителя, под которыми шёл обход:
 * курсор с другими не читает снимок. Размер `rows` держит сам обход (предохранитель
 * D4), а не схема: снимок, отвергнутый схемой, — это обход на каждой странице.
 */
export const dealQueueSnapshotSchema = z.object({
  query: z.object({
    status: z.string().nullable(),
    projectId: z.string().nullable(),
    includeDemo: z.string().nullable(),
    scope: z.string(),
  }),
  rows: z.array(z.tuple([z.string(), z.string()])),
  alsoServed: z.array(z.string()),
  buckets: dealQueueResponseSchema.shape.buckets,
  closed: dealQueueResponseSchema.shape.closed,
  truncated: z.boolean(),
});
export type DealQueueSnapshot = z.infer<typeof dealQueueSnapshotSchema>;

// ============================================================================
// Карточка: GET /api/v1/deals/:stateId
// ============================================================================

/**
 * Ссылки состояния, которых зритель не видит, маршрут убирает (Q6) и ставит
 * на пункт `sourceHidden`. Остальная форма — из `dealStateSchema`: изменение
 * хранимой формы не должно молча разойтись с ответом карточки.
 */
const hiddenFlag = { sourceHidden: z.boolean().optional() };

/**
 * Разговор-источник удалён (R10): интерфейс не рисует ссылку на запись, которой
 * нет, а пишет «разговор удалён». Зритель без границы по строкам цитату и id
 * всё ещё видит (#1750); зритель с границей — нет, но вместо `sourceHidden`
 * получает эту отметку (#1827). Необязателен — ответ прежнего edge остаётся
 * валидным; не путать с `sourceHidden` (разговор есть, зритель его не видит).
 * Нет у `basedOn`: его запись читается только как запасная ссылка цитаты
 * причины риска, а у той отметка своя.
 */
const deletedFlag = { sourceDeleted: z.boolean().optional() };

/**
 * Текст, который написала модель (причина риска, «почему» действия, кто и что
 * обещал) — `evidence.text`: управляемому субъекту без него шлюз возможностей
 * (`projectDealCard`) убирает эти поля и ставит `textWithheld: true` на
 * состояние. Поэтому поля необязательны в самих пунктах, а обязательность
 * полного ответа держит `superRefine` состояния: без флага текст на месте.
 */
export const visibleDealRiskReasonSchema = dealRiskReasonSchema.extend({
  text: dealRiskReasonSchema.shape.text.optional(),
  ...hiddenFlag,
  ...deletedFlag,
});

export const visibleDealNextActionSchema = dealNextActionSchema.extend({
  why: dealNextActionSchema.shape.why.optional(),
});

export const visibleDealCommitmentSchema = dealCommitmentSchema.extend({
  who: dealCommitmentSchema.shape.who.optional(),
  what: dealCommitmentSchema.shape.what.optional(),
  sourceRecordId: dealCommitmentSchema.shape.sourceRecordId.optional(),
  ...hiddenFlag,
  ...deletedFlag,
});

export const visibleDealObjectionSchema = dealObjectionSchema.extend({
  sourceRecordId: dealObjectionSchema.shape.sourceRecordId.optional(),
  ...hiddenFlag,
  ...deletedFlag,
});

// Без `continuedFrom` (#1813, `withoutLeadLink`): лид, которого продолжает
// сделка, нужен очереди на сервере, а зритель сделки может не видеть лида.
export const visibleDealStateSchema = dealStateSchema
  .omit({ continuedFrom: true })
  .extend({
    riskReason: visibleDealRiskReasonSchema.nullable(),
    nextAction: visibleDealNextActionSchema,
    commitments: z
      .array(visibleDealCommitmentSchema)
      .max(DEAL_STATE_LIMITS.commitmentsMax),
    objections: z.array(visibleDealObjectionSchema).max(DEAL_STATE_LIMITS.objectionsMax),
    basedOn: z.object({
      recordId: dealStateSchema.shape.basedOn.shape.recordId.optional(),
      at: dealStateSchema.shape.basedOn.shape.at,
      ...hiddenFlag,
    }),
    /**
     * `true` — зритель без `evidence.text`: текст модели (`riskReason.text`,
     * `nextAction.why`, `commitments[].who/what`, `qualification.*.fact`,
     * `talkPlan`, `changedSinceLast`, `quote`) убран, списки пусты.
     */
    textWithheld: z.boolean().optional(),
  })
  .superRefine((state, ctx) => {
    if (state.textWithheld === true) return;
    const missing = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: 'custom', path, message });
    if (state.riskReason && state.riskReason.text === undefined) {
      missing(['riskReason', 'text'], 'the risk text is withheld only with textWithheld');
    }
    if (state.nextAction.why === undefined) {
      missing(['nextAction', 'why'], 'the reason is withheld only with textWithheld');
    }
    state.commitments.forEach((item, index) => {
      if (item.who === undefined || item.what === undefined) {
        missing(['commitments', index], 'who/what are withheld only with textWithheld');
      }
    });
  });
export type VisibleDealState = z.infer<typeof visibleDealStateSchema>;

/**
 * Установка демо-сделок (#766 PR4c): метка `deal_states.installation_id` у
 * сделок, которые засеял демо-режим. Настоящей установки CRM за ней нет —
 * `embedded_installs` такого id не знает, и по метке сид находит свои строки
 * при очистке. Карточка и POST задачи судят по ней первой (`demo`), а не по
 * поиску установки, который сказал бы неправду «подключение изменилось».
 */
export const DEMO_DEAL_INSTALLATION_ID = 'demo';

/**
 * Закрытый список отказов 409 (D7): по нему интерфейс выбирает текст, а тест
 * маршрута обходит каждый. `unsupported_vendor` — CRM без задачи (не
 * Bitrix24 и не Uspacy). `crm_processing_inactive` — сделка Uspacy, чью
 * обработку клиент приостановил или отозвал (#2212): тот же код, что у прочих
 * маршрутов Uspacy (`CRM_PROCESSING_INACTIVE_CODE`). `demo` — сделка
 * демо-режима (PR4c): CRM нет, задачу писать некуда; кнопки в карточке нет,
 * отказ — для прямого запроса.
 */
export const DEAL_CRM_TASK_REFUSALS = [
  'not_head',
  'in_progress',
  'unsupported_vendor',
  'no_task_capability',
  'no_actionable_task',
  'target_gone',
  'installation_changed',
  'crm_processing_inactive',
  'demo',
] as const;
export type DealCrmTaskRefusal = (typeof DEAL_CRM_TASK_REFUSALS)[number];

/**
 * Почему карточка не предлагает кнопку задачи: отказы маршрута и
 * `not_allowed` — действие зрителю не разрешено (роль ∧ грант), хотя сделку он
 * видит: сотрудник с грантом всей организации смотрит сделку коллеги (ревью
 * Codex PR #1676). Маршрут на это отвечает 404/403, не 409.
 */
export const DEAL_CRM_TASK_UNAVAILABLE_REASONS = [
  ...DEAL_CRM_TASK_REFUSALS,
  'not_allowed',
  // Задача по этому состоянию уже создана (`task.status`/`task.taskId`); повторный
  // клик POST отвечает `alreadyCreated`, но предлагать его кнопкой незачем.
  'already_created',
] as const;
export type DealCrmTaskUnavailableReason =
  (typeof DEAL_CRM_TASK_UNAVAILABLE_REASONS)[number];

export const dealCardHeadSchema = z.object({
  stateId: stateIdSchema,
  entityType: entityTypeSchema,
  entityId: entityIdSchema,
  projectId: projectIdSchema,
  state: visibleDealStateSchema,
  /**
   * Шанс закрытия состояния, которое сменила голова, — «было N %» карточки.
   * Только у прошлого состояния ТОГО ЖЕ накопления (`facts_since`), как «было»
   * в CRM-комментарии (V9): после сброса фактов (E5) голова посчитана с нуля,
   * хотя `previousStateId` у неё остался, и её шанс с прежним не сравнивают.
   * У первого состояния сделки из лида (#1813) прошлое — состояние лида, и
   * только для зрителя, который видит разговор этого состояния.
   * Поля нет — сравнивать не с чем.
   */
  previousCloseChance: dealStateSchema.shape.closeChance.optional(),
  /**
   * Лид, из которого сконвертирована сделка (#1931), — «продолжение лида #N»
   * со ссылкой на его карточку: голова цепочки лида в проекте, поколении и
   * портале этой сделки. Только зрителю, которому открылась бы карточка лида
   * (то же правило, что у этой карточки). Поля нет — сделка не из лида, у лида
   * нет состояний в этом проекте или зритель его не видит.
   */
  continuedFromLead: z
    .object({ entityId: entityIdSchema, stateId: stateIdSchema })
    .optional(),
  lastConversationAt: isoDateTimeSchema,
  snapshot: dealSnapshotSchema,
  /** `buildCrmEntityUrl` по установке; `null` — нет домена или вендор без ссылки. */
  crmUrl: z.string().nullable(),
  /**
   * Справочник стадий организации (`GET /crm/stages`) описывает портал ЭТОЙ
   * сделки: её установка — та, что он открывает (`findFieldServiceInstall`:
   * активная, первого вендора со справочником). `false` — прежний портал или
   * другой вендор: те же id стадий (`0`, `C0:NEW`) там называют другое.
   */
  stageReferenceApplies: z.boolean(),
  task: z.object({
    status: z.enum(DEAL_STATE_TASK_STATUSES).nullable(),
    taskId: z.string().nullable(),
    at: isoDateTimeSchema.nullable(),
    /** Кнопка «Создать задачу в CRM» доступна (Bitrix24, голова, действие — не «подождать»). */
    available: z.boolean(),
    /**
     * Код из `DEAL_CRM_TASK_UNAVAILABLE_REASONS`, когда `available: false`.
     * `.catch(null)`: причину, добавленную сервером позже (`crm_processing_inactive`,
     * #2212), бандл, собранный до неё, читает как «недоступна без причины», а не
     * роняет на ней разбор всей карточки (перекос выкатки, открытая вкладка).
     */
    unavailableReason: z.enum(DEAL_CRM_TASK_UNAVAILABLE_REASONS).nullable().catch(null),
  }),
});
export type DealCardHead = z.infer<typeof dealCardHeadSchema>;

export const dealHistoryItemSchema = z.object({
  stateId: stateIdSchema,
  at: isoDateTimeSchema,
  status: dealStatusSchema,
  closeChance: dealStateSchema.shape.closeChance,
  changedSinceLast: z.array(z.string()),
  /** `null` — разговор зрителю не виден (Q6). */
  basedOnRecordId: recordIdSchema.nullable(),
  sourceHidden: z.boolean(),
  /**
   * Разговор удалён (R10): у зрителя без границы по строкам id остался, записи
   * нет (#1750); у зрителя с границей `basedOnRecordId: null`, `sourceHidden:
   * false` (#1827).
   */
  sourceDeleted: z.boolean().optional(),
  /** Позднее вливание: разговор старше соседних, строка пришла после них (D3). */
  lateFold: z.boolean(),
});
export type DealHistoryItem = z.infer<typeof dealHistoryItemSchema>;

export const dealConversationItemSchema = z.object({
  recordId: recordIdSchema,
  type: recordTypeSchema,
  createdAt: isoDateTimeSchema,
  /** Снимается полом шлюза без `team.scores` — см. `dealQueueItemSchema.agentName`. */
  agentName: z.string().nullable().optional(),
  status: z.string(),
  isShortCall: z.boolean(),
});
export type DealConversationItem = z.infer<typeof dealConversationItemSchema>;

/** Страница ленты разговоров — тот же вид у карточки и у `GET /deals/:stateId/conversations`. */
export const dealConversationsPageSchema = z.object({
  items: z.array(dealConversationItemSchema),
  nextCursor: z.string().nullable(),
});
export type DealConversationsPage = z.infer<typeof dealConversationsPageSchema>;

/**
 * Ответ `GET /deals/:stateId/conversations`: страница ленты и метка демо
 * закрытой функции (#2816), как у карточки.
 */
export const dealConversationsResponseSchema = dealConversationsPageSchema.extend({
  demoOnly: lockedDemoOnlySchema,
});
export type DealConversationsResponse = z.infer<typeof dealConversationsResponseSchema>;

export const dealCardResponseSchema = z.object({
  head: dealCardHeadSchema,
  history: z.array(dealHistoryItemSchema),
  conversations: dealConversationsPageSchema,
  demoOnly: lockedDemoOnlySchema,
});
export type DealCardResponse = z.infer<typeof dealCardResponseSchema>;

// ============================================================================
// Задача в CRM: POST /api/v1/deals/:stateId/crm-task
// ============================================================================

export const dealCrmTaskResponseSchema = z.object({
  status: z.literal('created'),
  /** `null` — задача найдена по маркеру, но её id поиск не вернул. */
  taskId: z.string().nullable(),
  /** Повторный клик: задача уже была заведена этим состоянием. */
  alreadyCreated: z.boolean(),
});
export type DealCrmTaskResponse = z.infer<typeof dealCrmTaskResponseSchema>;
